import React, { ReactNode, useContext, useEffect, useState } from 'react'
import MaskedInput from 'react-maskedinput'
import { join } from 'lodash'
import Select from 'live/components/forms/ui/select'
import i18n from 'live/services/i18n'
import { STATIC_DATA_STATES } from '../chart-components'
import { CustomDatePicker, ModalField, ModalSectionHeader } from '../modal-components'
import { fetchEntityOptions, RIG_TABLE_API } from '../../api/api'
import { dateAndTimeRegex, dateTimeToTimestamp, timestampToDateTime } from '../../utils/parse'
import { RawRigTableEvent, SectionOptionsTemplate, SelectOptions } from '../../utils/type'
import { StaticDataContext } from '../../context/StaticDataContext'

interface RigTableTabProps {
    event: RawRigTableEvent
    children?: ReactNode
    updateRigTableEvent: (event: RawRigTableEvent) => void
    hasPermissionToEdit: boolean
    selectedOptionWarning
    setSelectedOptionWarning
}
const RigTableTab: React.FC<RigTableTabProps> = ({
    event,
    updateRigTableEvent,
    hasPermissionToEdit,
    selectedOptionWarning,
    setSelectedOptionWarning
}) => {
    const [selectedEvent, setSelectedEvent] = useState<RawRigTableEvent>(event)
    const eventStartDateTime = event ? timestampToDateTime(event.start) : null
    const eventEndDateTime = event ? timestampToDateTime(event.end) : null
    const [startDate, setStartDate] = useState<string>(eventStartDateTime?.date || null)
    const [startHour, setStartHour] = useState<string>(eventStartDateTime?.time || null)
    const [endDate, setEndDate] = useState<string>(eventEndDateTime?.date || null)
    const [endHour, setEndHour] = useState<string>(eventEndDateTime?.time || null)
    const [statusTypeList, setStatusTypeList] = useState([])

    useEffect(() => {
        fetchEntityOptions(RIG_TABLE_API).then((e: SectionOptionsTemplate | null) => {
            if (!e) return
            setStatusTypeList(e.statusType as SelectOptions[])
        })
    }, [])

    useEffect(() => {
        updateRigTableEvent(selectedEvent)
    }, [selectedEvent])

    useEffect(() => {
        if (dateAndTimeRegex.test(join([startDate, startHour], ' '))) {
            const timestamp = dateTimeToTimestamp(join([startDate, startHour], ' '))
            setSelectedEvent(prevstate => ({ ...prevstate, start: timestamp }))
        }
    }, [startDate, startHour])

    useEffect(() => {
        if (dateAndTimeRegex.test(join([endDate, endHour], ' '))) {
            const timestamp = dateTimeToTimestamp(join([endDate, endHour], ' '))
            setSelectedEvent(prevstate => ({ ...prevstate, end: timestamp }))
        }
    }, [endDate, endHour])

    useEffect(() => {
        if (eventStartDateTime?.date !== startDate || eventStartDateTime?.time !== startHour) {
            setStartDate(eventStartDateTime?.date)
            setStartHour(eventStartDateTime?.time)
        }
    }, [eventStartDateTime?.date, eventStartDateTime?.time])

    return (
        <>
            <div onMouseDown={e => e.stopPropagation()}>
                <ModalSectionHeader label={i18n('Attributes')} />
                <ModalField
                    label={'Type'}
                    field={<input disabled placeholder={i18n('Main')} type="text" value={'Main'} />}
                />
                <ModalField
                    label={'RPI Path Status'}
                    field={
                        <Select
                            disabled={!hasPermissionToEdit}
                            searchable={false}
                            clearable={false}
                            options={statusTypeList}
                            placeholder={selectedEvent.status ? `${selectedEvent.status}` : i18n('Select')}
                            value={selectedEvent.status !== '' && selectedEvent.status}
                            onChange={value => {
                                setSelectedEvent(prev => ({ ...prev, status: value }))
                                setSelectedOptionWarning(false)
                            }}
                            style={selectedOptionWarning ? { borderColor: '#D9534F' } : {}}
                        />
                    }
                />
                <ModalField
                    field={
                        <div className="input-date-time">
                            <ModalField
                                label={'Start Date'}
                                field={<CustomDatePicker date={startDate} setDate={setStartDate} />}
                            />
                            <ModalField
                                label={'Start Hour'}
                                field={
                                    <MaskedInput
                                        disabled={
                                            !hasPermissionToEdit || selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                        }
                                        mask="11:11"
                                        type="text"
                                        placeholder={'00:00'}
                                        value={startHour}
                                        onChange={e => setStartHour(e.target.value)}
                                    />
                                }
                            />
                        </div>
                    }
                    enabled={false}
                />
                {selectedEvent.state != STATIC_DATA_STATES.EMPTY && (
                    <ModalField
                        style={{ margin: 0 }}
                        field={
                            <div className="input-date-time">
                                <ModalField
                                    label={'End Date'}
                                    field={
                                        <CustomDatePicker
                                            isDisabled={
                                                !hasPermissionToEdit ||
                                                selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                            }
                                            date={eventEndDateTime?.date}
                                            setDate={setEndDate}
                                        />
                                    }
                                />
                                <ModalField
                                    label={'End Hour'}
                                    field={
                                        <MaskedInput
                                            disabled={
                                                !hasPermissionToEdit ||
                                                selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                            }
                                            className={'masked-input'}
                                            mask="11:11"
                                            type="text"
                                            placeholder={'00:00'}
                                            value={eventEndDateTime?.time}
                                            onChange={e => setEndHour(e.target.value)}
                                        />
                                    }
                                />
                            </div>
                        }
                    />
                )}
                <div style={{ marginTop: -13 }} />
            </div>
        </>
    )
}

export default RigTableTab